import classNames from "classnames";
import MergeModal from "../../shared/MergeModal";
import { useState } from "react";
import { Form, Table } from "react-bootstrap";
import { CheckSquare, Copy, XSquare } from "lucide-react";
import { BlueprintRunnerAutogeneratedMappingTestInformation } from "../../../models/Blueprints";
import { navigateToMappingTestEditor } from "../../../router/RouterUtils";
import { useHistory } from "react-router-dom";
import useBlueprintContext from "../../blueprint-editor/context/useBlueprintContext";
import {
  Button,
  ButtonVariant,
  Accordion,
  Text,
  Tooltip,
} from "@merge-api/merge-javascript-shared";
import { MappingTestExecutionResult } from "../../../models/MappingTests";
import styled from "styled-components";
import { showSuccessToast } from "../Toasts";
import {
  BlueprintAdminEnvironment,
  getCurrentBlueprintAdminEnvironment,
} from "../../blueprint-editor/utils/BlueprintVersionEnvironmentLinks";

type ModelFieldInfo = {
  [commonModelID: string]:
    | Array<{ [fieldName: string]: any }>
    | { [uniqueID: string]: { [fieldName: string]: any } };
};

type CommonModelObjects = ModelFieldInfo[string];

interface Props {
  show: boolean;
  onHide: () => void;
  title: string;
  producedModelsTabs: {
    [tabName: string]: ModelFieldInfo;
  };
  mappingTestInfo?: undefined | BlueprintRunnerAutogeneratedMappingTestInformation;
  modelUpdates?: ModelFieldInfo;
  /**
   * Whether the run this data came from used a Merge-org Linked Account. Copying produced model
   * data is only offered for Merge-org Linked Accounts, never for real customer data.
   */
  isMergeOrgLinkedAccountSelected?: boolean;
}

// Copying produced model data is a develop-only debugging affordance
const COPY_ENABLED_ENVIRONMENTS = [
  BlueprintAdminEnvironment.Develop,
  BlueprintAdminEnvironment.MuDevelop,
];

const AccordionWrapper = styled.div`
  /* Pre-calculate width to prevent horizontal jump during animation */
  display: inline-flex;
  flex-direction: column;
  min-width: 100%;

  /* Target MUI Accordion components to prevent spacing shifts during animation */
  .MuiAccordion-root {
    margin: 0 !important;
    padding: 0 !important;
  }

  .MuiCollapse-root {
    margin-top: 0 !important;
    padding-top: 0 !important;
  }

  .MuiCollapse-wrapper {
    margin-top: 0 !important;
    padding-top: 0 !important;
  }

  .MuiCollapse-wrapperInner {
    margin-top: 0 !important;
    padding-top: 0 !important;
  }

  .MuiCollapse-entered,
  .MuiCollapse-hidden {
    margin-top: 0 !important;
    padding-top: 0 !important;
  }

  /* Ensure accordion summary (title) stays in place */
  .MuiAccordionSummary-root {
    transition: none !important;
  }

  /* Ensure accordion details have no top spacing */
  .MuiAccordionDetails-root {
    margin-top: 0 !important;
    padding-top: 0 !important;
  }

  /* During collapse animation, prevent sticky positioning from causing shifts */
  .MuiCollapse-root:not(.MuiCollapse-entered) {
    .sticky {
      position: relative !important;
      top: auto !important;
    }
  }

  .low-padding-table thead {
    border-top: none !important;
  }
`;

function IntegrationEditorProducedModelsModal({
  show,
  onHide,
  title,
  producedModelsTabs,
  mappingTestInfo,
  modelUpdates,
  isMergeOrgLinkedAccountSelected = false,
}: Props) {
  const { blueprint } = useBlueprintContext();
  const [activeTab, setActiveTab] = useState<number>(0);
  const [showRemoteDataInModal, setShowRemoteDataInModal] = useState<boolean>(false);
  const [showDeletedDataInModal, setShowDeletedDataInModal] = useState<boolean>(false);
  const history = useHistory();

  const ignoreColumns = [
    "linked_account_id",
    "deprecated_linked_account",
    "ignore_id",
    "custom_fields",
  ];

  function filterColumns(key: string) {
    if (ignoreColumns.includes(key)) return false;
    else if (key === "remote_data" && !showRemoteDataInModal) return false;
    else if (key === "remote_was_deleted" && !showDeletedDataInModal) return false;
    return true;
  }

  const activeTabModels = Object.values(producedModelsTabs)[activeTab];

  const currentEnvironment = getCurrentBlueprintAdminEnvironment();
  const canCopyProducedModels =
    isMergeOrgLinkedAccountSelected &&
    !!currentEnvironment &&
    COPY_ENABLED_ENVIRONMENTS.includes(currentEnvironment);

  // Drops the columns hidden by the toggles above so a copy matches what's rendered in the table
  function filterModelObjectsForCopy(commonModelObjects: CommonModelObjects) {
    const filterRow = (row: { [fieldName: string]: any }) =>
      Object.entries(row)
        .filter(([key]) => filterColumns(key))
        .reduce(
          (filteredRow, [key, value]) => ({ ...filteredRow, [key]: value }),
          {} as { [key: string]: any }
        );

    if (Array.isArray(commonModelObjects)) return commonModelObjects.map(filterRow);
    return Object.entries(commonModelObjects).reduce(
      (models, [uniqueID, row]) => ({ ...models, [uniqueID]: filterRow(row) }),
      {} as { [uniqueID: string]: any }
    );
  }

  const copyModel = (commonModelID: string, commonModelObjects: CommonModelObjects) => {
    navigator.clipboard.writeText(
      JSON.stringify(filterModelObjectsForCopy(commonModelObjects), null, 2)
    );
    showSuccessToast(`Copied ${commonModelID} to clipboard.`);
  };

  const copyAllModels = () => {
    const allModels = Object.entries(activeTabModels).reduce(
      (models, [commonModelID, commonModelObjects]) => ({
        ...models,
        [commonModelID]: filterModelObjectsForCopy(commonModelObjects),
      }),
      {} as { [commonModelID: string]: any }
    );
    navigator.clipboard.writeText(JSON.stringify(allModels, null, 2));
    showSuccessToast("Copied all models to clipboard.");
  };

  const doesGeneratedMappingTestExistsWithFailedRun =
    mappingTestInfo?.result === MappingTestExecutionResult.FAILURE;

  const doesGeneratedMappingTestExists =
    mappingTestInfo?.was_successful || doesGeneratedMappingTestExistsWithFailedRun;

  const navigateToMappingTest = doesGeneratedMappingTestExists
    ? () =>
        navigateToMappingTestEditor(
          history,
          blueprint.integration.id,
          mappingTestInfo.mapping_test_id,
          true
        )
    : undefined;

  return (
    <MergeModal
      title={title}
      dialogClassName="modal-xl"
      show={show}
      onHide={onHide}
      bodyClassName="max-h-[70vh] overflow-y-auto relative pt-0"
      nav={
        <ul className="nav nav-tabs nav-tabs-sm card-header-tabs">
          {Object.keys(producedModelsTabs).map((tabName, index) => (
            <li key={tabName} className="nav-item">
              <span
                className={classNames("nav-link", index === activeTab ? "active" : "")}
                onClick={() => setActiveTab(index)}
              >
                {tabName}
              </span>
            </li>
          ))}
        </ul>
      }
    >
      <div className="flex flex-row items-start justify-between">
        <div className="flex flex-col">
          <Form.Group className="mb-0" controlId="show-remote-data">
            <Form.Check
              type="checkbox"
              label="Show remote data"
              defaultChecked={showRemoteDataInModal}
              onChange={() => setShowRemoteDataInModal(!showRemoteDataInModal)}
            />
          </Form.Group>
          <Form.Group controlId="show-whether-deleted-data">
            <Form.Check
              type="checkbox"
              label="Show whether deleted data"
              defaultChecked={showRemoteDataInModal}
              onChange={() => setShowDeletedDataInModal(!showDeletedDataInModal)}
            />
          </Form.Group>
        </div>
        {canCopyProducedModels && Object.keys(activeTabModels).length > 0 && (
          <Button
            size="sm"
            variant={ButtonVariant.TertiaryWhite}
            leftIcon={<Copy size={12} />}
            onClick={copyAllModels}
          >
            Copy all as JSON
          </Button>
        )}
      </div>
      {Object.entries(activeTabModels).map(([commonModelID, commonModelObjects], index) => {
        return (
          <AccordionWrapper key={commonModelID} className="relative pr-[15px]">
            {index === 0 ? <hr className="mx-0 mb-2" /> : <div className="h-2 bg-white" />}
            <Accordion
              title={
                <div className="flex flex-row items-center space-x-2">
                  <Text variant="md" className="font-semibold">
                    {commonModelID} {`(${(Object.keys(commonModelObjects) ?? []).length})`}
                  </Text>
                  {canCopyProducedModels && (
                    /* Stops the click from toggling the Accordion open/closed */
                    <span
                      className="flex items-center"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <Tooltip title={`Copy ${commonModelID} data as JSON`}>
                        <Button
                          variant={ButtonVariant.IconShadowHidden}
                          className="bg-transparent"
                          onClick={() => copyModel(commonModelID, commonModelObjects)}
                        >
                          <Copy size={14} className="text-gray-40" />
                        </Button>
                      </Tooltip>
                    </span>
                  )}
                </div>
              }
              titleClassName="sticky top-0 bg-white z-[100] p-2"
              defaultExpanded={false}
              chevronOrientation="right"
              variant="none"
            >
              <div className="mt-0 pt-0">
                <Table size="sm" className="low-padding-table mt-0 mb-0 w-full border-t-0">
                  <thead>
                    <tr>
                      {Object.keys(Object.values(commonModelObjects ?? {})?.[0] ?? [])
                        .filter(function (key) {
                          return filterColumns(key);
                        })
                        .sort(function (a, b) {
                          const keyA = a,
                            keyB = b;
                          if (keyA === "created") return -1;
                          if (keyB === "created") return -1;
                          return 0;
                        })
                        .map((key) => (
                          <th
                            key={key}
                            scope="col"
                            className="sticky-table-header sticky top-[32px] z-[90] p-2 border-0"
                            style={{ backgroundColor: "#95a5b8", color: "white" }}
                          >
                            {key}
                          </th>
                        ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(commonModelObjects).map(
                      ([commonModelUniqueIdentifier, commonModelRow]) => {
                        return (
                          <tr key={commonModelUniqueIdentifier}>
                            {Object.entries(commonModelRow)
                              .filter(function (entry) {
                                return filterColumns(entry[0]);
                              })
                              .sort(function (a, b) {
                                const keyA = a[0],
                                  keyB = b[0];

                                if (keyA === "created") return -1;
                                if (keyB === "created") return -1;
                                return 0;
                              })
                              .map(([key, value]) => {
                                const updatedFields = modelUpdates?.[commonModelID];
                                let isUpdated = false;
                                if (
                                  updatedFields &&
                                  !Array.isArray(updatedFields) &&
                                  updatedFields[commonModelUniqueIdentifier]
                                ) {
                                  isUpdated = key in updatedFields[commonModelUniqueIdentifier];
                                }
                                // 0 value decimals in Python are written to string as scientific notation (e.g. 0E-16 or 0E-8).
                                // This can be confusing in the model updates panel, so we catch these cases with regex here and overwrite the value to "0.0".
                                if (/^0E-?\d+$/.test(value)) {
                                  value = "0.0";
                                }
                                return (
                                  <td key={key} className={isUpdated ? "yellow" : undefined}>
                                    {key === "created" ? (
                                      value === "true" ? (
                                        <CheckSquare size={18} className="green" />
                                      ) : (
                                        <XSquare size={18} className="text-muted" />
                                      )
                                    ) : (
                                      JSON.stringify(value)
                                    )}
                                  </td>
                                );
                              })}
                          </tr>
                        );
                      }
                    )}
                  </tbody>
                </Table>
              </div>
            </Accordion>
            <hr className="-mx-[15px]" />
          </AccordionWrapper>
        );
      })}
      {mappingTestInfo &&
        (navigateToMappingTest ? (
          <div className="flex flex-col">
            {doesGeneratedMappingTestExistsWithFailedRun && (
              <div className="mb-3">
                The mapping test and associated models were successfully generated, however the run
                failed. In most cases, this is due to slightly incorrect assertions or mismatched
                request mocks and can be easily fixed in order to take advantage of the
                autogenerated test. Please still post in #mapping-test-discussions with your
                payload, linked account, and blueprint so we can work to improve this.{" "}
              </div>
            )}
            <Button variant={ButtonVariant.PrimaryGray} onClick={navigateToMappingTest}>
              View autogenerated mapping test
            </Button>
          </div>
        ) : (
          <div>
            Mapping test generation failed with exceptions. Please post in #mapping-test-discussions
            with the payload, linked account, and blueprint you were using.{" "}
          </div>
        ))}
    </MergeModal>
  );
}

export default IntegrationEditorProducedModelsModal;
